// ---------------------------------------------------------------------------
// Test Mode — module-level state (CLE-250).
//
// `apiFetch` (lib/api.ts) is a plain async function, not a React component, so
// it can't read context. This tiny module holds the Test Mode flag in a
// module-level variable backed by localStorage, letting both the data layer and
// the React `TestModeProvider` share one source of truth.
//
// When Test Mode is ON, apiFetch short-circuits to the simulated mock router
// instead of hitting the real backend — the real API is never called, so live
// data is never touched.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'ccc.test-mode';

function readInitial(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

let testModeEnabled = readInitial();

type Listener = (enabled: boolean) => void;
const listeners = new Set<Listener>();

/** Synchronous read used by the data layer (apiFetch). */
export function isTestModeEnabled(): boolean {
  return testModeEnabled;
}

/** Toggle / set Test Mode, persist it and notify subscribers. */
export function setTestModeEnabled(enabled: boolean): void {
  if (enabled === testModeEnabled) return;
  testModeEnabled = enabled;
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    // Ignore storage failures (private mode, quota) — state still lives in memory.
  }
  for (const listener of listeners) listener(enabled);
}

/** Subscribe to changes (used by the React provider). Returns an unsubscribe fn. */
export function subscribeTestMode(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
